import { BleTransport } from '../ble/transport';
import {
  buildStartCommand,
  buildStopCommand,
  CommandAckCode,
  FirmwareAckCode,
  makeSectorPackets,
  OTA_SECTOR_SIZE,
  parseCommandAck,
  parseFirmwareAck,
  verifyAckCrc,
} from './protocol';
import { OtaAbortError, OtaSessionOptions, OtaUpdateResult } from './types';

type PendingCommandAck = {
  resolve: () => void;
  reject: (error: Error) => void;
};

type PendingFirmwareAck = {
  expectedIndex: number;
  resolve: () => void;
  reject: (error: Error) => void;
};

export class OtaSession {
  private inProgress = false;
  private pendingCommandAck: PendingCommandAck | null = null;
  private pendingFirmwareAck: PendingFirmwareAck | null = null;

  constructor(private transport: BleTransport, private options: OtaSessionOptions = {}) {}

  async run(file: ArrayBuffer, signal?: AbortSignal): Promise<OtaUpdateResult> {
    if (this.inProgress) {
      throw new Error('An OTA session is already running.');
    }
    if (!file || file.byteLength === 0) {
      throw new Error('Firmware file is empty.');
    }

    this.inProgress = true;
    const startedAt = performance.now();
    const totalBytes = file.byteLength;
    let bytesSent = 0;

    try {
      await this.transport.subscribeCommandNotifications(this.handleCommandAck);
      await this.transport.subscribeFirmwareNotifications(this.handleFirmwareAck);

      this.log('info', 'OTA: start command');
      await this.sendCommand(buildStartCommand(totalBytes), signal);

      const fileBytes = new Uint8Array(file);
      const totalSectors = Math.ceil(fileBytes.length / OTA_SECTOR_SIZE);
      const maxRetries = this.options.maxRetries ?? 2;

      for (let sectorIndex = 0; sectorIndex < totalSectors; sectorIndex += 1) {
        this.throwIfAborted(signal);
        const start = sectorIndex * OTA_SECTOR_SIZE;
        const end = Math.min(fileBytes.length, start + OTA_SECTOR_SIZE);
        const sector = fileBytes.subarray(start, end);

        let attempt = 0;
        let sectorDone = false;
        while (!sectorDone) {
          attempt += 1;
          try {
            if (attempt > 1) {
              this.log('info', `Retrying sector ${sectorIndex + 1}/${totalSectors} (attempt ${attempt}).`);
            }
            await this.sendSector(sector, sectorIndex, signal);
            sectorDone = true;
          } catch (error) {
            if (attempt > maxRetries) {
              throw error;
            }
          }
        }

        bytesSent = end;
        const elapsedSeconds = (performance.now() - startedAt) / 1000;
        const speedKbps = elapsedSeconds > 0 ? (bytesSent / 1024) / elapsedSeconds : 0;
        const percent = (bytesSent / totalBytes) * 100;
        this.options.onProgress?.({
          percent,
          bytesSent,
          totalBytes,
          speedKbps,
          sectorIndex,
        });
        if ((sectorIndex + 1) % 25 === 0) {
          this.log('info', `Sector ${sectorIndex + 1}/${totalSectors}`);
        }
      }

      this.status('Finalizing update...', 'info');
      this.log('info', 'OTA: stop command');
      await this.sendCommand(buildStopCommand(), signal);
      this.options.onComplete?.();

      this.status('Update complete. The device may reboot to apply the update.', 'success');
      this.log('success', 'OTA: update complete');
      return { durationMs: performance.now() - startedAt, bytesSent };
    } catch (error) {
      if (error instanceof OtaAbortError) {
        this.status('Update canceled.', 'warning');
        this.log('error', 'OTA: update canceled');
      } else {
        const message = error instanceof Error ? error.message : 'OTA failed.';
        this.status(`Update failed: ${message}`, 'error');
        this.log('error', `OTA: failed (${message})`);
      }
      throw error;
    } finally {
      this.inProgress = false;
    }
  }

  private async sendCommand(payload: Uint8Array, signal?: AbortSignal): Promise<void> {
    this.throwIfAborted(signal);
    const ackPromise = this.waitForCommandAck(this.options.ackTimeoutMs ?? 5000, signal);
    await this.transport.writeCommand(payload);
    await ackPromise;
  }

  private async sendSector(
    sector: Uint8Array,
    sectorIndex: number,
    signal?: AbortSignal
  ): Promise<void> {
    this.throwIfAborted(signal);
    const ackPromise = this.waitForFirmwareAck(sectorIndex, this.options.ackTimeoutMs ?? 5000, signal);
    for (const packetInfo of makeSectorPackets(sector, sectorIndex)) {
      this.throwIfAborted(signal);
      await this.transport.writeFirmware(packetInfo.packet);
    }
    await ackPromise;
  }

  private waitForCommandAck(timeoutMs: number, signal?: AbortSignal): Promise<void> {
    this.clearPendingCommandAck();

    return new Promise((resolve, reject) => {
      let pending: PendingCommandAck | null = null;
      let settled = false;
      const timeoutId = window.setTimeout(() => {
        cleanup(new Error('Command ack timeout.'));
      }, timeoutMs);
      const abortHandler = () => cleanup(new OtaAbortError());
      if (signal) {
        signal.addEventListener('abort', abortHandler, { once: true });
      }

      const cleanup = (error?: Error) => {
        if (settled) {
          return;
        }
        settled = true;
        window.clearTimeout(timeoutId);
        if (signal) {
          signal.removeEventListener('abort', abortHandler);
        }
        if (pending && this.pendingCommandAck === pending) {
          this.pendingCommandAck = null;
        }
        if (error) {
          reject(error);
        } else {
          resolve();
        }
      };

      pending = {
        resolve: () => cleanup(),
        reject: (error: Error) => cleanup(error),
      };
      this.pendingCommandAck = pending;
    });
  }

  private waitForFirmwareAck(
    expectedIndex: number,
    timeoutMs: number,
    signal?: AbortSignal
  ): Promise<void> {
    this.clearPendingFirmwareAck();

    return new Promise((resolve, reject) => {
      let pending: PendingFirmwareAck | null = null;
      let settled = false;
      const timeoutId = window.setTimeout(() => {
        cleanup(new Error('Firmware ack timeout.'));
      }, timeoutMs);
      const abortHandler = () => cleanup(new OtaAbortError());
      if (signal) {
        signal.addEventListener('abort', abortHandler, { once: true });
      }

      const cleanup = (error?: Error) => {
        if (settled) {
          return;
        }
        settled = true;
        window.clearTimeout(timeoutId);
        if (signal) {
          signal.removeEventListener('abort', abortHandler);
        }
        if (pending && this.pendingFirmwareAck === pending) {
          this.pendingFirmwareAck = null;
        }
        if (error) {
          reject(error);
        } else {
          resolve();
        }
      };

      pending = {
        expectedIndex,
        resolve: () => cleanup(),
        reject: (error: Error) => cleanup(error),
      };
      this.pendingFirmwareAck = pending;
    });
  }

  private handleCommandAck = (view: DataView): void => {
    if (!this.pendingCommandAck) {
      return;
    }
    if (view.byteLength < 20 || !verifyAckCrc(view)) {
      this.pendingCommandAck.reject(new Error('Command ack CRC mismatch.'));
      return;
    }
    const ack = parseCommandAck(view);
    if (!ack) {
      return;
    }
    if (ack.code === CommandAckCode.Ok) {
      this.pendingCommandAck.resolve();
    } else if (ack.code === CommandAckCode.SignatureError) {
      this.pendingCommandAck.reject(new Error('Signature error reported by device.'));
    } else {
      this.pendingCommandAck.reject(new Error(`Command rejected (${ack.rawCode}).`));
    }
  };

  private handleFirmwareAck = (view: DataView): void => {
    if (!this.pendingFirmwareAck) {
      return;
    }
    if (view.byteLength < 20 || !verifyAckCrc(view)) {
      this.pendingFirmwareAck.reject(new Error('Firmware ack CRC mismatch.'));
      return;
    }
    const ack = parseFirmwareAck(view);
    if (ack.ackIndex !== this.pendingFirmwareAck.expectedIndex) {
      this.pendingFirmwareAck.reject(
        new Error(`Firmware ack index mismatch (got ${ack.ackIndex}).`)
      );
      return;
    }
    if (ack.code === FirmwareAckCode.Ok) {
      this.pendingFirmwareAck.resolve();
    } else {
      this.pendingFirmwareAck.reject(new Error(describeFirmwareAck(ack.code)));
    }
  };

  private status(message: string, level: 'info' | 'success' | 'warning' | 'error'): void {
    this.options.onStatus?.({ message, level });
  }

  private log(level: 'info' | 'success' | 'error', message: string): void {
    this.options.onLog?.({ level, message });
  }

  private throwIfAborted(signal?: AbortSignal): void {
    if (signal?.aborted) {
      throw new OtaAbortError();
    }
  }

  private clearPendingCommandAck(): void {
    if (this.pendingCommandAck) {
      this.pendingCommandAck.reject(new Error('Command ack superseded.'));
    }
  }

  private clearPendingFirmwareAck(): void {
    if (this.pendingFirmwareAck) {
      this.pendingFirmwareAck.reject(new Error('Firmware ack superseded.'));
    }
  }
}

function describeFirmwareAck(code: FirmwareAckCode): string {
  switch (code) {
    case FirmwareAckCode.CrcError:
      return 'Firmware CRC error reported by device.';
    case FirmwareAckCode.IndexMismatch:
      return 'Firmware index mismatch reported by device.';
    case FirmwareAckCode.LengthError:
      return 'Firmware payload length error reported by device.';
    default:
      return 'Firmware ack rejected by device.';
  }
}
